import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';

import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { DashboardService } from '@completion/services';
import {
  DashboardActionTypes,
  LoadDashboardCheckSheetAssignmentStatus,
  LoadDashboardCheckSheetAssignmentStatusSuccess,
  LoadDashboardCps,
  LoadDashboardCpsSuccess,
  LoadDashboardMcps,
  LoadDashboardMcpsSuccess,
  LoadPrePunches,
  LoadPrePunchesSuccess,
  LoadUserPunches,
  LoadUserPunchesSuccess,
  LoadUserSignatureAssignments,
  LoadUserSignatureAssignmentsSuccess,
  PunchActionTypes,
} from '@completion/actions';
import { DashboardPackage, Project, Punch, UserSignatureAssignments } from '@completion/models';
import { trackResource } from './resource-fetch.util';
import { DashboardCheckSheetAssignmentStatus } from '../../../shared/models/dashboard-checksheet-status';

@Injectable()
export class DashboardEffect {
  constructor(
    private actions$: Actions,
    private dashboardService: DashboardService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadDashboardCps$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDashboardCps>(DashboardActionTypes.LoadDashboardCps),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<DashboardPackage[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.dashboardService,
        method: 'getDashboardCps',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadDashboardCpsSuccess(response),
      }),
    ),
  );

  loadDashboardMcps$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDashboardMcps>(DashboardActionTypes.LoadDashboardMcps),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<DashboardPackage[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.dashboardService,
        method: 'getDashboardMcps',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadDashboardMcpsSuccess(response),
      }),
    ),
  );

  loadUserPunches$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadUserPunches>(PunchActionTypes.LoadUserPunches),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<Punch[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.dashboardService,
        method: 'getUserPunches',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadUserPunchesSuccess(response),
      }),
    ),
  );

  loadPrePunches$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPrePunches>(PunchActionTypes.LoadPrePunches),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<Punch[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.dashboardService,
        method: 'getPrePunches',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadPrePunchesSuccess(response),
      }),
    ),
  );

  loadUserSignatureAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadUserSignatureAssignments>(DashboardActionTypes.LoadUserSignatureAssignments),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<UserSignatureAssignments>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.dashboardService,
        method: 'getUserSignatureAssignments',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadUserSignatureAssignmentsSuccess(response),
      }),
    ),
  );

  loadDashboardCheckSheetAssignmentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDashboardCheckSheetAssignmentStatus>(DashboardActionTypes.LoadDashboardCheckSheetAssignmentStatus),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource<DashboardCheckSheetAssignmentStatus>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.dashboardService,
        method: 'getDashboardCheckSheetAssignmentStatus',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadDashboardCheckSheetAssignmentStatusSuccess(response),
      }),
    ),
  );
}
